import { debugApi } from './networkLogger'
/**
 * Кэш витрин на стороне планшета: память + `localStorage`.
 *
 * ЗАЧЕМ ПЕРСИСТ, А НЕ ТОЛЬКО ПАМЯТЬ. Витрины ЦН считаются в БД десятками
 * секунд (education — около 40 с, public-safety доходит до минуты), а данные
 * в них суточные. Кэш только в памяти умирает на каждом F5 и при возврате к
 * приложению с планшета, и человек снова смотрит на минутный спиннер ради тех
 * же цифр. В `localStorage` результат переживает перезагрузку вкладки.
 *
 * ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО: ничего пользовательского и ничего, что меняется
 * в течение минут. Сюда кладутся только обезличенные агрегаты витрин.
 */

const PREFIX = 'sc_cache:'

/**
 * Версия формата. Меняется вместе с формой ответов бэкенда: иначе после
 * выкатки нового поля планшет ещё час рисует старый объект из `localStorage`
 * и падает на `undefined` там, где ждёт новое поле.
 */
const VERSION = 'v1'

type Entry<T> = {
  v: string
  /** Время записи, мс. */
  t: number
  /** Сколько запись считается свежей, мс. */
  ttl: number
  data: T
}

/** Часовой TTL — значение по умолчанию для витрин. */
export const SCREEN_CACHE_TTL = 60 * 60 * 1000

/*
 * Параллельные запросы одного ключа схлопываются в один поход на бэкенд.
 * Без этого модуль с тремя блоками на одном источнике открывает три запроса
 * по 40 с вместо одного — кэш при этом не помогает, он заполняется позже.
 */
const inFlight = new Map<string, Promise<unknown>>()

function storage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    // Приватный режим и заблокированные куки: чтение `localStorage` кидает.
    return null
  }
}

export function readCache<T>(key: string): T | null {
  const store = storage()
  if (!store) return null
  try {
    const raw = store.getItem(PREFIX + key)
    if (!raw) return null
    const entry = JSON.parse(raw) as Entry<T>
    if (entry.v !== VERSION || Date.now() - entry.t > entry.ttl) {
      store.removeItem(PREFIX + key)
      return null
    }
    return entry.data
  } catch {
    return null
  }
}

export function writeCache<T>(key: string, data: T, ttl = SCREEN_CACHE_TTL): void {
  const store = storage()
  if (!store) return
  try {
    const entry: Entry<T> = { v: VERSION, t: Date.now(), ttl, data }
    store.setItem(PREFIX + key, JSON.stringify(entry))
  } catch {
    /*
     * Квота кончилась (у витрин ЦН ответы до сотен килобайт). Это не повод
     * ронять экран: чистим свои записи и живём дальше без персиста — данные
     * уже отданы вызывающему коду.
     */
    clearScreenCache()
  }
}

/** Точечная инвалидация: все ключи, содержащие подстроку. */
export function invalidateScreenCache(fragment: string): void {
  const store = storage()
  if (!store) return
  try {
    for (const key of Object.keys(store)) {
      if (key.startsWith(PREFIX) && key.includes(fragment)) store.removeItem(key)
    }
  } catch {
    /* см. `storage()` */
  }
}

/** Полная очистка — на выходе пользователя и при смене учётной записи. */
export function clearScreenCache(): void {
  const store = storage()
  if (!store) return
  try {
    for (const key of Object.keys(store)) {
      if (key.startsWith(PREFIX)) store.removeItem(key)
    }
  } catch {
    /* см. `storage()` */
  }
}

/**
 * Обёртка для сервисов, которые ходят голым `fetch` мимо `apiRequest`:
 * отдаёт свежий кэш, схлопывает параллельные вызовы, при ошибке загрузки
 * ничего не кэширует.
 */
export async function cachedCall<T>(
  key: string,
  loader: () => Promise<T>,
  ttl = SCREEN_CACHE_TTL,
): Promise<T> {
  const hit = readCache<T>(key)
  if (hit !== null) {
    debugApi('storage', 'SCREEN', key)
    return hit
  }

  const pending = inFlight.get(key) as Promise<T> | undefined
  if (pending) return pending

  const promise = loader()
    .then((data) => {
      writeCache(key, data, ttl)
      inFlight.delete(key)
      return data
    })
    .catch((error) => {
      inFlight.delete(key)
      throw error
    })

  inFlight.set(key, promise)
  return promise
}
